import { supabase } from '../lib/supabase';

export const emailVerificationService = {
  async sendVerificationEmail(email, fullName, verificationToken) {
    try {
      const { data, error } = await supabase?.functions?.invoke('send-verification-email', {
        body: {
          email,
          fullName,
          verificationToken
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to send verification email' } };
    }
  },

  async verifyEmailToken(token) {
    try {
      // Get current user
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user profile with verification token
      const { data: profile, error: fetchError } = await supabase
        ?.from('user_profiles')
        ?.select('email_verification_token, email_verification_expires_at, email_verified')
        ?.eq('id', user?.id)
        ?.single();

      if (fetchError) throw fetchError;

      // Check if already verified
      if (profile?.email_verified) {
        return { data: { verified: true, message: 'Email already verified' }, error: null };
      }

      // Check if token matches
      if (profile?.email_verification_token !== token) {
        return { data: null, error: { message: 'Invalid verification code' } };
      }

      // Check if token expired
      const expiresAt = new Date(profile?.email_verification_expires_at);
      if (expiresAt < new Date()) {
        return { data: null, error: { message: 'Verification code has expired' } };
      }

      // Update user profile to mark email as verified
      const { error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({
          email_verified: true,
          email_verification_token: null,
          email_verification_sent_at: null,
          email_verification_expires_at: null
        })
        ?.eq('id', user?.id);

      if (updateError) throw updateError;

      return { data: { verified: true, message: 'Email verified successfully' }, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Verification failed' } };
    }
  },

  async resendVerificationEmail() {
    try {
      // Get current user
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Generate new token for this resend request
      const { data: tokenData, error: tokenError } = await supabase?.rpc('generate_verification_token');
      if (tokenError) throw tokenError;

      const newToken = tokenData;

      // Update user profile with fresh token
      const { error: updateError } = await supabase
        ?.from('user_profiles')
        ?.update({
          email_verification_token: newToken,
          email_verification_sent_at: new Date()?.toISOString(),
          email_verification_expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString() // 24 hours
        })
        ?.eq('id', user?.id);

      if (updateError) throw updateError;

      // Get user profile for email and name
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('email, full_name')
        ?.eq('id', user?.id)
        ?.single();

      if (profileError) throw profileError;

      // Send new verification email with fresh token
      return await this.sendVerificationEmail(profile?.email, profile?.full_name, newToken);
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to resend verification email' } };
    }
  },

  async checkVerificationStatus() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('email_verified, email')
        ?.eq('id', user?.id)
        ?.single();

      if (error) throw error;

      return {
        data: {
          emailVerified: data?.email_verified,
          email: data?.email
        },
        error: null
      };
    } catch (error) {
      return { data: null, error: { message: error?.message || 'Failed to check verification status' } };
    }
  }
};

export default emailVerificationService;